// src/services/rdoStorageSupabase.ts
//
// Substituto ASSÍNCRONO e drop-in do rdoStorageService (localStorage), gravando
// no Supabase (tabela public.rdos). Mantém o MESMO formato LEGADO de RDO usado
// pelas páginas (RDO.tsx / PreenchendoRDO.tsx): o documento legado inteiro é
// guardado em `payload` (jsonb); obra_id/date/status/approved são espelhados em
// colunas para filtro/ordenação e RLS (por user_id).
//
// As funções têm os mesmos nomes do rdoStorageService, porém retornam Promise.
import { supabase } from "@/integrations/supabase/client";
import { normalizeRDO } from "@/domain/rdo/normalizeRDO";
import type { RDO, RDOCreateInput } from "@/services/rdoStorageService";

const TABLE = "rdos" as const;

async function requireUserId(): Promise<string> {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user) {
    throw new Error("[rdoStorageSupabase] Usuário não autenticado. Faça login para acessar os RDOs.");
  }
  return user.id;
}

// Linha do banco -> RDO legado (colunas têm prioridade; resto vem do payload).
// Passa por normalizeRDO: payloads antigos/incompletos não podem quebrar as
// telas que fazem .map()/.reduce() direto nos arrays.
function rowToRDO(row: any): RDO {
  const payload = (row?.payload ?? {}) as Partial<RDO>;
  return normalizeRDO({
    ...payload,
    id: row.id,
    obraId: row.obra_id,
    date: row.date,
    status: (row.status ?? payload.status ?? "draft") as RDO["status"],
    approved: row.approved ?? payload.approved ?? false,
    approvedBy: row.approved_by ?? payload.approvedBy ?? null,
    approvedAt: row.approved_at ?? payload.approvedAt ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  });
}

// RDO legado -> payload jsonb (remove o id; demais campos ficam no documento)
function toPayload(rdo: Partial<RDO>): any {
  const clone: any = { ...rdo };
  delete clone.id;
  return clone;
}

export async function getRDOs(): Promise<RDO[]> {
  const { data, error } = await supabase.from(TABLE).select("*").order("date", { ascending: false });
  if (error) throw new Error(`[rdoStorageSupabase] getRDOs: ${error.message}`);
  return (data ?? []).map(rowToRDO);
}

export async function listRDOs(obraId: string): Promise<RDO[]> {
  const { data, error } = await supabase
    .from(TABLE).select("*").eq("obra_id", obraId).order("date", { ascending: false });
  if (error) throw new Error(`[rdoStorageSupabase] listRDOs: ${error.message}`);
  return (data ?? []).map(rowToRDO);
}

export async function getLastRDO(obraId: string): Promise<RDO | null> {
  const { data, error } = await supabase
    .from(TABLE).select("*").eq("obra_id", obraId)
    .order("date", { ascending: false }).limit(1).maybeSingle();
  if (error) throw new Error(`[rdoStorageSupabase] getLastRDO: ${error.message}`);
  return data ? rowToRDO(data) : null;
}

export async function getRDOById(id: string): Promise<RDO | null> {
  const { data, error } = await supabase.from(TABLE).select("*").eq("id", id).maybeSingle();
  if (error) throw new Error(`[rdoStorageSupabase] getRDOById: ${error.message}`);
  return data ? rowToRDO(data) : null;
}

export async function getRDOByObraDate(obraId: string, date: string): Promise<RDO | null> {
  const { data, error } = await supabase
    .from(TABLE).select("*").eq("obra_id", obraId).eq("date", date).maybeSingle();
  if (error) throw new Error(`[rdoStorageSupabase] getRDOByObraDate: ${error.message}`);
  return data ? rowToRDO(data) : null;
}

export async function createRDO(input: RDOCreateInput): Promise<RDO> {
  const userId = await requireUserId();
  const status = (input.status ?? "draft") as RDO["status"];
  const { data, error } = await supabase.from(TABLE).insert({
    user_id: userId,
    obra_id: input.obraId,
    date: input.date,
    status,
    approved: false,
    schema_version: 1,
    payload: toPayload({ ...(input as Partial<RDO>), status }),
  }).select("*").single();
  if (error) throw new Error(`[rdoStorageSupabase] createRDO: ${error.message}`);
  return rowToRDO(data);
}

export async function updateRDO(id: string, updates: Partial<RDO>): Promise<RDO | null> {
  const current = await getRDOById(id);
  if (!current) return null;
  const merged: RDO = { ...current, ...updates };
  const { data, error } = await supabase.from(TABLE).update({
    obra_id: merged.obraId,
    date: merged.date,
    status: (merged.status ?? "draft") as string,
    approved: merged.approved ?? false,
    approved_by: merged.approvedBy ?? null,
    approved_at: merged.approvedAt ?? null,
    payload: toPayload(merged),
  }).eq("id", id).select("*").maybeSingle();
  if (error) throw new Error(`[rdoStorageSupabase] updateRDO: ${error.message}`);
  return data ? rowToRDO(data) : null;
}

export async function deleteRDO(id: string): Promise<boolean> {
  const { error } = await supabase.from(TABLE).delete().eq("id", id);
  if (error) {
    console.error("[rdoStorageSupabase] deleteRDO:", error.message);
    return false;
  }
  return true;
}
